import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, Cone, TrainFront, ArrowRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const REFRESH_MS = 90_000;

/**
 * LiveTravelStrip — homepage one-liner that mirrors the live counts from the
 * /roads-and-travel page (live incidents · roadworks · transport).
 *
 * - Always renders, so the page never has an awkward gap on calm days. When
 *   nothing's happening it shows an "all clear" pill.
 * - Fetches on mount + every 90s with cache: 'no-store'.
 * - Click anywhere on the strip routes to /roads-and-travel.
 */
const LiveTravelStrip = () => {
  const [counts, setCounts] = useState({ incidents: 0, roadworks: 0, transport: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    const load = () => fetch(`${API}/roads-and-travel`, { cache: 'no-store' })
      .then((r) => r.json())
      .then((d) => {
        if (!alive) return;
        setCounts({
          incidents: (d.incidents || []).length,
          roadworks: (d.roadworks || []).length,
          transport: (d.transport || []).length,
        });
      })
      .catch(() => {})
      .finally(() => alive && setLoading(false));
    load();
    const id = setInterval(load, REFRESH_MS);
    return () => { alive = false; clearInterval(id); };
  }, []);

  const total = counts.incidents + counts.roadworks + counts.transport;
  const allClear = !loading && total === 0;

  return (
    <section className="relative max-w-7xl mx-auto px-6 mb-4" data-testid="live-travel-strip">
      <Link
        to="/roads-and-travel"
        className="group flex flex-wrap items-center justify-between gap-3 rounded-2xl px-5 py-3 bg-slate-900 hover:bg-slate-800 text-white ring-1 ring-white/10 transition-colors shadow-sm"
      >
        <div className="flex items-center gap-3 min-w-0 flex-wrap">
          <span className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.2em] text-slate-300">
            <span className={`w-2 h-2 rounded-full ${allClear ? 'bg-emerald-400' : 'bg-red-500 animate-pulse'}`} />
            Live travel
          </span>
          {allClear ? (
            <span className="text-sm text-slate-200">
              <span className="font-semibold text-emerald-300">All clear</span>
              <span className="text-slate-400 ml-2">no incidents reported across Paisley &amp; Renfrewshire</span>
            </span>
          ) : (
            <div className="flex items-center gap-4 text-sm" data-testid="live-travel-counts">
              {counts.incidents > 0 && (
                <span className="inline-flex items-center gap-1.5 text-red-300">
                  <AlertTriangle size={13} />
                  <span className="font-bold">{counts.incidents}</span>
                  <span className="text-slate-300">incident{counts.incidents === 1 ? '' : 's'}</span>
                </span>
              )}
              {counts.roadworks > 0 && (
                <span className="inline-flex items-center gap-1.5 text-amber-300">
                  <Cone size={13} />
                  <span className="font-bold">{counts.roadworks}</span>
                  <span className="text-slate-300">roadwork{counts.roadworks === 1 ? '' : 's'}</span>
                </span>
              )}
              {counts.transport > 0 && (
                <span className="inline-flex items-center gap-1.5 text-cyan-300">
                  <TrainFront size={13} />
                  <span className="font-bold">{counts.transport}</span>
                  <span className="text-slate-300">transport</span>
                </span>
              )}
            </div>
          )}
        </div>
        <span
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-300 group-hover:text-white transition-colors"
          data-testid="live-travel-cta"
        >
          View live updates
          <ArrowRight size={13} className="group-hover:translate-x-0.5 transition-transform" />
        </span>
      </Link>
    </section>
  );
};

export default LiveTravelStrip;
